import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { SectionHeading } from '../SectionHeading';
import { PRODUCT_IMAGES, productModules } from '../../data/products';
function ModuleLink({
  label,
  href
}: {
  label: string;
  href: string;
}) {
  return <Link to={href} data-analytics="product-category-click" data-analytics-label={label} className="mt-auto inline-flex min-h-[44px] items-center gap-2 self-start border-b border-copper pt-6 text-[14px] font-semibold text-navy transition-colors duration-150 ease-precise hover:text-copper" data-id="element-235" data-mp="1hkedjgogr7g0">
      {label}
      <ArrowRightIcon aria-hidden="true" className="h-4 w-4" data-id="element-236" data-mp="12wtatnvi3a8m" />
    </Link>;
}
function ModuleFrame({
  index,
  heading,
  children,
  className = ''
}: {
  index: string;
  heading: string;
  children: React.ReactNode;
  className?: string;
}) {
  return <article className={`flex flex-col border border-rule bg-white p-6 lg:p-9 ${className}`} data-id="element-237" data-mp="2a9vdczeexqm9">
      <div className="flex items-baseline justify-between gap-4 border-b border-rule pb-4" data-id="element-238" data-mp="1h5zy8f4sp4l5">
        <h3 className="font-display text-[22px] font-medium leading-tight text-navy lg:text-[26px]" data-id="element-239" data-mp="1m4b9muaqnn5h">
          {heading}
        </h3>
        <span className="font-mono text-[10px] tracking-label text-copper" data-id="element-240" data-mp="1q7g9ep3fpaqf">
          {index}
        </span>
      </div>
      {children}
    </article>;
}
function SpecimenImage({
  src,
  alt,
  caption,
  maxWidth
}: {
  src: string;
  alt: string;
  caption: string;
  maxWidth: string;
}) {
  return <figure className="rp-grid-lines mt-6 border border-rule bg-warm p-5" style={{
    maxWidth
  }} data-id="element-241" data-mp="12hdkuiyqamwk">
      <img src={src} alt={alt} loading="lazy" className="h-auto w-full border border-rule bg-white" data-id="element-242" data-mp="17ecc8tr0cfr2" />
      <figcaption className="mt-3 font-mono text-[9.5px] tracking-label text-graphite/60" data-id="element-243" data-mp="er9g289t0h3z">
        {caption}
      </figcaption>
    </figure>;
}
export function ProductSystem() {
  const [kits, holders, inserts, accessories] = productModules;
  return <section id="products" aria-labelledby="products-heading" className="w-full bg-warm" data-id="element-244" data-mp="1mzf9w94ogp1i">
      <div className="mx-auto max-w-shell px-5 py-16 lg:px-10 lg:py-24" data-id="element-245" data-mp="1hzsym58etivc">
        <SectionHeading id="products-heading" eyebrow="PRODUCT SYSTEM" heading="Build the Shear Geometry setup your application needs" intro="Start with the shank size, then review the published roughing, chipbreaker or titanium-oriented insert. These product numbers and configurations come from Robertson Precision’s legacy published catalog. Confirm current availability, compatibility, packaging, pricing and lead time directly before ordering." data-id="element-246" data-mp="1vs0l945zbtgb" />

        <div className="mt-14 grid gap-5 lg:grid-cols-12" data-id="element-247" data-mp="18266gh23nutf">
          {/* Starter kits — lead module */}
          <ModuleFrame index={kits.index} heading={kits.heading} className="lg:col-span-7" data-id="element-248" data-mp="2ogehbedicfp8">
            <div className="grid gap-8 sm:grid-cols-[minmax(0,1fr)_minmax(0,220px)]" data-id="element-249" data-mp="2rzwvmrpgfc3y">
              <div className="flex flex-col" data-id="element-250" data-mp="2s29jflhw8gox">
                <p className="mt-6 text-[15.5px] leading-[1.65] text-graphite/85" data-id="element-251" data-mp="2w2jngsm4kx15">
                  {kits.lead}
                </p>
                <ul className="mt-6 space-y-3" data-id="element-252" data-mp="2i3k3hy9k56bb">
                  {kits.details.map((detail, i) => <li key={detail} className={`flex gap-3 text-[14px] leading-[1.6] ${i < 2 ? 'font-mono text-navy rp-tabular' : 'text-graphite/80'}`} data-id="element-253" data-mp="2n2wmg7xb5t8m" data-mpi={i}>
                      <span aria-hidden="true" className="mt-2.5 h-px w-3 shrink-0 bg-copper" data-id="element-254" data-mp="35zocd07cv6ln" />
                      <span data-id="element-255" data-mp="3tnxu9r1g7a7i">{detail}</span>
                    </li>)}
                </ul>
                <ModuleLink label={kits.linkLabel} href={kits.href} data-id="element-256" data-mp="2bw8i8e3tnv5" />
              </div>
              <SpecimenImage src={PRODUCT_IMAGES.holder} alt="Shear Geometry tool holder included with the published starter kits" caption="KIT HOLDER — LEGACY PHOTO" maxWidth="220px" data-id="element-257" data-mp="29vpjh1rixnm4" />
            </div>
          </ModuleFrame>

          {/* Tool holders */}
          <ModuleFrame index={holders.index} heading={holders.heading} className="lg:col-span-5" data-id="element-258" data-mp="2ti5fiqg2yrrv">
            <p className="mt-6 text-[15.5px] leading-[1.65] text-graphite/85" data-id="element-259" data-mp="2okdf181ddxc2">
              {holders.lead}
            </p>
            <dl className="mt-6 divide-y divide-rule border-y border-rule" data-id="element-260" data-mp="2jqhh25l3wagw">
              <div className="flex items-baseline justify-between gap-4 py-3" data-id="element-261" data-mp="2esg2nxymkzbr">
                <dt className="font-mono text-[13.5px] text-navy" data-id="element-262" data-mp="3c8wkzgwy5y0z">
                  SALXR 16-3D
                </dt>
                <dd className="font-mono rp-tabular text-[12.5px] text-graphite/70" data-id="element-263" data-mp="37h4wdbxcq2dh">
                  1-INCH SHANK
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-4 py-3" data-id="element-264" data-mp="3jrw4rnoblrkv">
                <dt className="font-mono text-[13.5px] text-navy" data-id="element-265" data-mp="3jtz3qx7gav1q">
                  SALXR 12-3B
                </dt>
                <dd className="font-mono rp-tabular text-[12.5px] text-graphite/70" data-id="element-266" data-mp="3tq2726e1gzq9">
                  3/4-INCH SHANK
                </dd>
              </div>
            </dl>
            <p className="mt-4 text-[14px] leading-[1.6] text-graphite/80" data-id="element-267" data-mp="1kgdteph6l24">
              Uses low-profile insert screws and a T-15 Torx driver.
            </p>
            <ModuleLink label={holders.linkLabel} href={holders.href} data-id="element-268" data-mp="bdnj6zk7u127" />
          </ModuleFrame>

          {/* Inserts & chipbreakers */}
          <ModuleFrame index={inserts.index} heading={inserts.heading} className="lg:col-span-8" data-id="element-269" data-mp="gdid7bzaf6rh">
            <p className="mt-6 text-[15.5px] leading-[1.65] text-graphite/85" data-id="element-270" data-mp="pbc6ee1r9kaf">
              {inserts.lead}
            </p>
            <div className="mt-2 grid gap-8 lg:grid-cols-[minmax(0,176px)_minmax(0,1fr)]" data-id="element-271" data-mp="2md9k10vyflw5">
              <div className="order-2 lg:order-1" data-id="element-272" data-mp="2hey90mtxb5cc">
                <figure className="rp-grid-lines mt-4 border border-rule bg-warm p-4 sm:max-w-[220px] lg:mt-4 lg:max-w-none" data-id="element-273" data-mp="2v51qz1lrscvz">
                  <div className="aspect-square w-full border border-rule bg-white" data-id="element-274" data-mp="2qe36dptr0smx">
                    <img src={PRODUCT_IMAGES.insert} alt="Polished APGX insert from the legacy Shear Geometry catalog" loading="lazy" className="h-full w-full object-contain" data-id="element-275" data-mp="352pxspwnhaui" />
                  </div>
                  <figcaption className="mt-3 font-mono text-[9.5px] tracking-label text-graphite/60" data-id="element-276" data-mp="3a22jv8fp6dw1">
                    APGX INSERT — LEGACY PHOTO
                  </figcaption>
                </figure>
              </div>
              <div className="order-1 flex flex-col lg:order-2" data-id="element-277" data-mp="1fpmf6c2jv7ta">
                <ul className="mt-4 divide-y divide-rule border-y border-rule" data-id="element-278" data-mp="15juncvlh1cna">
                  {inserts.details.map(sku => {
                  const [name, radius] = sku.split(' — ');
                  return <li key={sku} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2.5" data-id="element-279" data-mp="vti7hgolq7nh">
                        <span className="font-mono text-[13px] leading-snug text-navy" data-id="element-280" data-mp="lw3xnzx6oh45">
                          {name}
                        </span>
                        <span className="font-mono rp-tabular text-[12.5px] text-graphite/70" data-id="element-281" data-mp="qt3v5mfywbjn">
                          {radius}
                        </span>
                      </li>;
                })}
                </ul>
                <p className="mt-4 text-[13.5px] leading-[1.6] text-graphite/70" data-id="element-282" data-mp="279gg5jfvaro6">
                  {inserts.note}
                </p>
                <ModuleLink label={inserts.linkLabel} href={inserts.href} data-id="element-283" data-mp="2c81jvsubvthd" />
              </div>
            </div>
          </ModuleFrame>

          {/* Accessories */}
          <ModuleFrame index={accessories.index} heading={accessories.heading} className="lg:col-span-4" data-id="element-284" data-mp="3fv6vmwifp3uo">
            <p className="mt-6 text-[15.5px] leading-[1.65] text-graphite/85" data-id="element-285" data-mp="3b12pgfgzszun">
              {accessories.lead}
            </p>
            <ul className="mt-6 divide-y divide-rule border-y border-rule" data-id="element-286" data-mp="323aa5bb9ts79">
              {accessories.details.map(item => <li key={item} className="py-3 font-mono text-[13.5px] text-navy" data-id="element-287" data-mp="2x2wc8m3dvymy">
                  {item}
                </li>)}
            </ul>
            <ModuleLink label={accessories.linkLabel} href={accessories.href} data-id="element-288" data-mp="2o4sp5lcr2tt1" />
          </ModuleFrame>
        </div>
      </div>
    </section>;
}